import { unref, type MaybeRef, type MaybeRefOrGetter, type Ref } from 'vue';
import { queryOptions } from '@tanstack/vue-query';
import DocumentLegacyApi from '@/modules/document/api/document-api.ts';
import { PriorityQueryKey, TypeQueryKey } from '@/modules/common/enums/query-key.enum.ts';
import { DocumentApi } from '@/modules/document/api/document.api.ts';
import DocumentMasterApi from '@/modules/document/api/document-master-api.ts';
import { RouteApi } from '@/modules/shared/feature-bridges.ts';

export class DocumentQueryOptions {
  static BASE_KEY = 'document';

  static getTypes(signal?: AbortSignal, enabled?: MaybeRefOrGetter<boolean>) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'type'],
      queryFn: (meta) => DocumentApi.getTypes(signal ?? meta.signal),
      enabled,
    });
  }

  static getMask(masks: Ref<string[]>) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'mask', masks],
      queryFn: (meta) => DocumentLegacyApi.getMasks(masks.value, meta),
    });
  }

  static getStatuses(signal?: AbortSignal) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'status'],
      queryFn: (meta) => DocumentApi.getStatuses(signal ?? meta.signal),
    });
  }

  static getReports(documentTypeId: number) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'reports', documentTypeId],
      queryFn: (meta) => DocumentLegacyApi.getReports(documentTypeId, meta.signal),
    });
  }

  static checkById(id: number) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'check', id],
      queryFn: (meta) => DocumentApi.checkById(id, meta.signal),
    });
  }

  static getColorState(id: MaybeRef<number>) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, TypeQueryKey.Color, id],
      queryFn: (meta) => DocumentApi.getColorState(unref(id), meta.signal),
    });
  }

  static getFiles(id: Ref<number>, enabled?: MaybeRefOrGetter<boolean>) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'files', id],
      queryFn: (meta) => DocumentLegacyApi.getFiles(id.value, meta.signal),
      enabled,
    });
  }

  static getFileTemplates(typeId: Ref<number | undefined>, enabled?: MaybeRefOrGetter<boolean>) {
    return queryOptions({
      queryKey: [PriorityQueryKey.Secondary, DocumentQueryOptions.BASE_KEY, 'file-templates', typeId],
      queryFn: async (meta) => {
        const types = await DocumentMasterApi.getTypes(meta.signal);
        const docIds = types.find((type) => type.code === typeId.value)?.docIds ?? [];

        return docIds.length ? RouteApi.getFileTemplates(docIds, meta.signal, true) : [];
      },
      enabled,
    });
  }
}
